/**
 * GuestFilesManager — UI completa de adjuntos multimedia para la sección
 * "Información para el huésped" del Modal de propiedad.
 *
 * QUÉ HACE
 *   Carga, sube, previsualiza, descarga (uno o varios), selecciona y borra
 *   los archivos asociados a una propiedad. Los archivos viven en
 *   `propiedades.info_huesped_archivos` (JSONB) del backend y se sirven con
 *   URLs firmadas vía `/files/<token>` (6h TTL).
 *
 * UX (decisiones tomadas en round 2026-05-29):
 *   1. Zona principal de upload — DragDropUpload + Ctrl+V paste activos
 *      mientras el modal de propiedad esté abierto. Convención del proyecto
 *      (docs/storage-y-visor.md §7.1). Acepta imágenes, videos, PDFs,
 *      Office y texto en una sola zona unificada (en vez de 3 zonas
 *      separadas) para no fragmentar el flujo del admin.
 *   2. Grid responsivo de cards — cada archivo se ve como una "tarjeta"
 *      con thumbnail (imagen/video) o ícono de tipo (PDF/Doc/Excel/etc.),
 *      nombre original, tamaño y badges. Click sobre la card abre el
 *      preview lightbox (imágenes/videos) o pestaña nueva (docs).
 *   3. Selección múltiple — checkbox arriba a la izquierda de cada card.
 *      "Seleccionar todo" en la barra superior. Acciones bulk: descargar
 *      ZIP + eliminar selección.
 *   4. Descarga — botón individual por archivo (forzando ?d=1 para
 *      Content-Disposition: attachment) + botón global "Descargar todos"
 *      que arma un ZIP con `downloadFilesAsZip` (lazy-load de jszip).
 *   5. Borrado — confirmación Popconfirm individual; bulk con Modal.confirm
 *      cuando son varios (popconfirm no se ve sobre selección flotante).
 *   6. Estado vacío — placeholder amigable con instrucciones en vez de
 *      grid vacío que confunde.
 *   7. Loading granular — overlay solo sobre la card siendo eliminada,
 *      spinner solo en la zona de upload mientras se sube, no bloquea
 *      el modal entero.
 *
 * SEGURIDAD / VISIBILIDAD
 *   El componente se monta dentro del Modal de propiedad que SOLO se
 *   abre para admin/superadmin (botón "Editar" oculto al cliente). El
 *   backend `requireRole("administrador")` es el enforce real; este
 *   componente confía en eso.
 *
 * NOTA SOBRE PROPS
 *   `propiedadId`: si es null/undefined (estamos creando una propiedad
 *   nueva, sin id todavía), mostramos un info Alert explicando que hay
 *   que guardar primero antes de poder adjuntar archivos. Esto evita
 *   el problema clásico de "upload a borrador sin id" — mantenemos el
 *   flujo lineal: crear → guardar → editar y agregar archivos.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Empty,
  Image as AntImage,
  Modal as AntModal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  DragDropUpload,
  useClipboardPaste,
} from "../../../Components/DragDropUpload";
import VideoPlayer from "../../../Components/VideoPlayer";
import { getModal, getMsg } from "../../../utils/antdStatic";
import {
  buildDownloadUrl,
  propiedadesApi,
  PropertyInfoHuespedFile,
} from "../../../services/propiedades.api";
import {
  downloadFile,
  downloadFilesAsZip,
} from "../../../utils/downloadHelpers";

const { Text, Paragraph } = Typography;

// Tipos MIME aceptados — mantener sincronizado con el `fileFilter` de
// `routes/property.routes.js > uploadInfoHuesped`. Si agregas más al
// backend, agrégalos también aquí para que el browser los pre-filtre y
// muestre los archivos correctos en el file picker.
const ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/bmp," +
  "video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,video/3gpp,video/3gpp2," +
  "application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "text/plain,text/csv";

// Límite cliente — coincide con el `multer.limits.fileSize` del backend
// (100 MB). El check cliente evita una request fallida cuando el archivo
// claramente no va a pasar; el backend igual lo re-valida.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

// Formateo humano: 1024 → "1.0 KB", 1048576 → "1.0 MB", etc.
function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  // 1 decimal salvo para bytes (que no tiene sentido decimal).
  return unitIdx === 0
    ? `${Math.round(value)} ${units[unitIdx]}`
    : `${value.toFixed(1)} ${units[unitIdx]}`;
}

// Ícono según mime — para documentos no-thumbnaileables. Mostrar el
// formato real (Word/Excel/PDF/etc.) acelera la búsqueda visual.
function FileIcon({
  file,
  size = 36,
}: {
  file: PropertyInfoHuespedFile;
  size?: number;
}): React.ReactElement {
  const mime = file.mimeType || "";
  const style = { fontSize: size, color: "#8c8c8c" };
  if (file.kind === "image")
    return <FileImageOutlined style={{ ...style, color: "#13c2c2" }} />;
  if (file.kind === "video")
    return <VideoCameraOutlined style={{ ...style, color: "#722ed1" }} />;
  if (mime === "application/pdf")
    return <FilePdfOutlined style={{ ...style, color: "#cf1322" }} />;
  if (mime.includes("word"))
    return <FileWordOutlined style={{ ...style, color: "#1677ff" }} />;
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return <FileExcelOutlined style={{ ...style, color: "#52c41a" }} />;
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return <FilePptOutlined style={{ ...style, color: "#fa8c16" }} />;
  if (mime.includes("zip"))
    return <FileZipOutlined style={{ ...style, color: "#faad14" }} />;
  if (mime.startsWith("text/"))
    return <FileTextOutlined style={{ ...style, color: "#595959" }} />;
  return <FileOutlined style={style} />;
}

// Label corto del tipo, mostrado como Tag en cada card.
function kindLabel(file: PropertyInfoHuespedFile): {
  text: string;
  color: string;
} {
  if (file.kind === "image") return { text: "Imagen", color: "cyan" };
  if (file.kind === "video") return { text: "Video", color: "purple" };
  const mime = file.mimeType || "";
  if (mime === "application/pdf") return { text: "PDF", color: "red" };
  if (mime.includes("word")) return { text: "Word", color: "blue" };
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return { text: "Excel", color: "green" };
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return { text: "PPT", color: "orange" };
  if (mime.startsWith("text/")) return { text: "Texto", color: "default" };
  return { text: "Doc", color: "default" };
}

interface GuestFilesManagerProps {
  /**
   * ID de la propiedad. Null/undefined cuando se está creando una propiedad
   * nueva — el componente muestra un Alert pidiendo guardar primero.
   */
  propiedadId: number | null | undefined;
  /**
   * `true` mientras el modal padre esté abierto. Controla el listener de
   * paste (Ctrl+V) para que solo se active cuando el usuario está
   * efectivamente editando esta propiedad.
   */
  modalOpen: boolean;
}

const GuestFilesManager: React.FC<GuestFilesManagerProps> = ({
  propiedadId,
  modalOpen,
}) => {
  const [files, setFiles] = useState<PropertyInfoHuespedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Set de `key` de archivos que están siendo eliminados (overlay individual).
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  // Set de `key` seleccionados — controla las acciones bulk de la toolbar.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // Preview lightbox: solo para imágenes. Para videos abrimos un modal con
  // <VideoPlayer> (con selector de calidad). Para docs abrimos pestaña nueva (ya que el visor
  // gview/iframe requiere navegar a una ruta dedicada).
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] =
    useState<PropertyInfoHuespedFile | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // ── Carga inicial ─────────────────────────────────────────────────────
  // Re-fetcheamos cada vez que cambia el id o se abre/cierra el modal —
  // así las URLs firmadas no caducan entre sesiones largas del admin.
  const reload = useCallback(async () => {
    if (!propiedadId) {
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      const res = await propiedadesApi.getInfoHuespedArchivos(propiedadId);
      setFiles(res?.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [propiedadId]);

  useEffect(() => {
    if (modalOpen && propiedadId) {
      reload();
    } else {
      // Al cerrar el modal limpiamos la selección y el estado para que el
      // siguiente abrir empiece fresh (evita "fugas" entre propiedades
      // editadas en secuencia).
      setSelectedKeys(new Set());
      setPreviewImage(null);
      setPreviewVideo(null);
    }
  }, [modalOpen, propiedadId, reload]);

  // ── Upload ────────────────────────────────────────────────────────────
  // Subimos archivos uno a uno (no en paralelo) para mostrar feedback
  // claro al admin que arrastra varios a la vez — el orden de inserción
  // se respeta y los toasts no se atropellan. Si una falla, seguimos con
  // las siguientes (mismo patrón que el ZIP downloader).
  const handleUpload = useCallback(
    async (file: File) => {
      if (!propiedadId) {
        getMsg()?.warning("Guardá la propiedad antes de adjuntar archivos");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        getMsg()?.error(
          `"${file.name}" pesa ${formatBytes(file.size)} — el máximo permitido es ${formatBytes(MAX_UPLOAD_BYTES)}.`,
        );
        return;
      }
      setUploading(true);
      try {
        const uploaded = await propiedadesApi.uploadInfoHuespedArchivo(
          propiedadId,
          file,
        );
        if (uploaded) {
          // Optimistic append — evita un round trip extra al GET.
          setFiles((prev) => [...prev, uploaded]);
          getMsg()?.success(`"${file.name}" subido correctamente`);
        }
      } finally {
        setUploading(false);
      }
    },
    [propiedadId],
  );

  // Paste handler — gateado a `modalOpen && !!propiedadId` para no
  // interferir con otros componentes con paste activo (improbable, pero
  // explícito > implícito).
  useClipboardPaste({
    active: modalOpen && !!propiedadId,
    onFile: handleUpload,
    accept: ACCEPT,
  });

  // ── Selección ─────────────────────────────────────────────────────────
  const toggleSelected = useCallback((key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const allSelected =
    files.length > 0 && selectedKeys.size === files.length;
  const partiallySelected = selectedKeys.size > 0 && !allSelected;

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedKeys(checked ? new Set(files.map((f) => f.key)) : new Set());
    },
    [files],
  );

  // ── Acciones — descarga ──────────────────────────────────────────────
  const handleDownloadOne = useCallback(
    async (file: PropertyInfoHuespedFile) => {
      try {
        // Forzamos `?d=1` para que el backend mande Content-Disposition:
        // attachment. El helper `downloadFile` además infiere extensión
        // si el nombre original no la trae.
        await downloadFile(buildDownloadUrl(file), file.originalName);
      } catch (e: any) {
        getMsg()?.error(e?.message || "No se pudo descargar el archivo");
      }
    },
    [],
  );

  const downloadSelectedAsZip = useCallback(async () => {
    const targets = files.filter((f) => selectedKeys.has(f.key));
    if (targets.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        targets.map((f) => ({
          url: buildDownloadUrl(f),
          name: f.originalName,
        })),
        `propiedad-${propiedadId}-info-huesped-seleccion`,
      );
      getMsg()?.success(`${targets.length} archivo(s) empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  }, [files, selectedKeys, propiedadId]);

  const downloadAllAsZip = useCallback(async () => {
    if (files.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        files.map((f) => ({
          url: buildDownloadUrl(f),
          name: f.originalName,
        })),
        `propiedad-${propiedadId}-info-huesped`,
      );
      getMsg()?.success(`${files.length} archivo(s) empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  }, [files, propiedadId]);

  // ── Acciones — borrado ────────────────────────────────────────────────
  const handleDeleteOne = useCallback(
    async (file: PropertyInfoHuespedFile) => {
      if (!propiedadId) return;
      setDeletingKeys((prev) => new Set(prev).add(file.key));
      try {
        const updated = await propiedadesApi.deleteInfoHuespedArchivo(
          propiedadId,
          file.key,
        );
        setFiles(updated);
        setSelectedKeys((prev) => {
          const next = new Set(prev);
          next.delete(file.key);
          return next;
        });
      } finally {
        setDeletingKeys((prev) => {
          const next = new Set(prev);
          next.delete(file.key);
          return next;
        });
      }
    },
    [propiedadId],
  );

  const handleDeleteSelected = useCallback(() => {
    const targets = files.filter((f) => selectedKeys.has(f.key));
    if (targets.length === 0) return;
    getModal()?.confirm({
      title: `¿Eliminar ${targets.length} archivo(s)?`,
      content:
        "Esta acción es definitiva — los archivos se borrarán del servidor y no se pueden recuperar.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        // Borramos en serie para que el optimistic-update y el toast por
        // borrado no rompan el orden visual. Es un puñado de archivos —
        // la latencia agregada es trivial.
        for (const file of targets) {
          // eslint-disable-next-line no-await-in-loop
          await handleDeleteOne(file);
        }
      },
    });
  }, [files, selectedKeys, handleDeleteOne]);

  // ── Acciones — abrir / previsualizar ─────────────────────────────────
  const openFile = useCallback((file: PropertyInfoHuespedFile) => {
    if (file.kind === "image") {
      setPreviewImage(file.url);
    } else if (file.kind === "video") {
      setPreviewVideo(file);
    } else {
      // Docs: abrir en pestaña nueva. El visor unificado
      // (`/visor/...`) está acoplado a `contratos`/`tareas` — para los
      // adjuntos de propiedad simplemente abrimos la URL firmada, que el
      // browser maneja con su PDF viewer nativo.
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  }, []);

  // ── Render helpers ────────────────────────────────────────────────────
  const headerActions = useMemo(() => {
    const hasFiles = files.length > 0;
    const hasSelection = selectedKeys.size > 0;
    return (
      <Space wrap size="small">
        <Checkbox
          indeterminate={partiallySelected}
          checked={allSelected}
          disabled={!hasFiles}
          onChange={(e) => toggleSelectAll(e.target.checked)}
        >
          <Text>
            {hasSelection
              ? `Seleccionados ${selectedKeys.size}/${files.length}`
              : "Seleccionar todo"}
          </Text>
        </Checkbox>
        <Tooltip title="Descargar la selección como ZIP">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!hasSelection}
            loading={downloadingAll}
            onClick={downloadSelectedAsZip}
          >
            Descargar ({selectedKeys.size})
          </Button>
        </Tooltip>
        <Tooltip title="Empaquetar todos los archivos en un solo ZIP">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!hasFiles}
            loading={downloadingAll}
            onClick={downloadAllAsZip}
          >
            Descargar todos
          </Button>
        </Tooltip>
        <Tooltip title="Eliminar definitivamente la selección">
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!hasSelection}
            onClick={handleDeleteSelected}
          >
            Eliminar ({selectedKeys.size})
          </Button>
        </Tooltip>
      </Space>
    );
  }, [
    files.length,
    selectedKeys.size,
    partiallySelected,
    allSelected,
    toggleSelectAll,
    downloadSelectedAsZip,
    downloadAllAsZip,
    handleDeleteSelected,
    downloadingAll,
  ]);

  // ── Estado: propiedad nueva sin id ────────────────────────────────────
  if (!propiedadId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Guarda la propiedad antes de adjuntar archivos"
        description="Una vez creada, abrila para editar y vas a poder subir fotos, videos, PDFs y documentos en esta sección."
      />
    );
  }

  return (
    <div className="guest-files-manager">
      {/* ─── Zona de upload — drag&drop + click + Ctrl+V ─────────────── */}
      <DragDropUpload
        accept={ACCEPT}
        multiple
        loading={uploading}
        onFile={handleUpload}
        title="Arrastra fotos, videos o documentos aquí para subirlos"
        hint="También funcionan: click para elegir, o Ctrl+V para pegar (capturas, archivos copiados)"
      />

      {/* ─── Toolbar de selección + acciones bulk ─────────────────────── */}
      <div className="guest-files-toolbar">
        <Space size="middle" wrap>
          <Badge
            count={files.length}
            showZero
            style={{ backgroundColor: files.length ? "#1677ff" : "#bfbfbf" }}
          >
            <Text strong style={{ paddingRight: 8 }}>
              Archivos
            </Text>
          </Badge>
          {loading && <Spin size="small" />}
        </Space>
        {headerActions}
      </div>

      {/* ─── Grid de archivos o estado vacío ───────────────────────────── */}
      {files.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Todavía no hay archivos adjuntos.
              <br />
              Subí imágenes, videos o documentos para complementar las instrucciones escritas.
            </Paragraph>
          }
        />
      ) : (
        <div className="guest-files-grid">
          {files.map((file) => {
            const isSelected = selectedKeys.has(file.key);
            const isDeleting = deletingKeys.has(file.key);
            const { text: kindText, color: kindColor } = kindLabel(file);
            return (
              <div
                key={file.key}
                className={`guest-files-card${isSelected ? " is-selected" : ""}`}
              >
                {/* Checkbox top-left para selección bulk */}
                <Checkbox
                  className="guest-files-card-check"
                  checked={isSelected}
                  onChange={(e) => toggleSelected(file.key, e.target.checked)}
                  aria-label={`Seleccionar ${file.originalName}`}
                />

                {/* Thumbnail / ícono — click abre preview */}
                <button
                  type="button"
                  className="guest-files-card-thumb"
                  onClick={() => openFile(file)}
                  title={`Abrir ${file.originalName}`}
                >
                  {file.kind === "image" && file.thumbUrl ? (
                    <img
                      src={file.thumbUrl}
                      alt={file.originalName}
                      loading="lazy"
                      draggable={false}
                    />
                  ) : file.kind === "video" && file.thumbUrl ? (
                    <div className="guest-files-card-video-thumb">
                      <img
                        src={file.thumbUrl}
                        alt={file.originalName}
                        loading="lazy"
                        draggable={false}
                      />
                      <PlayCircleOutlined className="guest-files-card-play" />
                    </div>
                  ) : (
                    <div className="guest-files-card-icon">
                      <FileIcon file={file} size={48} />
                    </div>
                  )}
                </button>

                {/* Pie con nombre + tamaño + tag + acciones */}
                <div className="guest-files-card-meta">
                  <Tooltip title={file.originalName}>
                    <Text ellipsis className="guest-files-card-name">
                      {file.originalName}
                    </Text>
                  </Tooltip>
                  <div className="guest-files-card-row">
                    <Tag color={kindColor} style={{ marginRight: 0 }}>
                      {kindText}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatBytes(file.size)}
                    </Text>
                  </div>
                  <Space size={4} style={{ marginTop: 6 }} wrap>
                    <Tooltip title="Abrir / previsualizar">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openFile(file)}
                      />
                    </Tooltip>
                    <Tooltip title="Descargar">
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadOne(file)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="¿Eliminar este archivo?"
                      description="No se puede recuperar después."
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteOne(file)}
                    >
                      <Tooltip title="Eliminar">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isDeleting}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>

                {/* Overlay loading mientras se elimina */}
                {isDeleting && (
                  <div className="guest-files-card-overlay">
                    <Spin />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Preview lightbox de imagen ────────────────────────────────── */}
      {/*
        Usamos el `<Image>` de antd en modo controlado para que se renderice
        ya con el preview abierto y permita zoom/rotate sin necesidad de
        un visor custom. Lo mantenemos hidden hasta que el usuario hace
        click sobre una imagen.
      */}
      {previewImage && (
        <AntImage
          src={previewImage}
          style={{ display: "none" }}
          preview={{
            visible: !!previewImage,
            src: previewImage,
            onVisibleChange: (vis) => {
              if (!vis) setPreviewImage(null);
            },
          }}
        />
      )}

      {/* ─── Preview modal de video ────────────────────────────────────── */}
      <AntModal
        open={!!previewVideo}
        onCancel={() => setPreviewVideo(null)}
        title={previewVideo?.originalName ?? "Video"}
        footer={null}
        width={900}
        style={{ top: 24 }}
        destroyOnClose
      >
        {previewVideo && (
          <VideoPlayer
            src={previewVideo.url}
            previewSrc={previewVideo.previewUrl}
            nombreArchivo={previewVideo.originalName}
            sizeBytes={previewVideo.size}
            previewSizeBytes={previewVideo.previewSize}
            altoMaximo="calc(100dvh - 220px)"
            autoPlay
            playsInline
          />
        )}
      </AntModal>
    </div>
  );
};

export default GuestFilesManager;
